<template>
  <div
    class="plan-item"
    draggable="true"
    @mousedown="dragStart"
    @touchstart="dragStart"
    @contextmenu.prevent="onContextMenu"
    v-bind:style="{ left: currentDragPosition.left + 'px', top: currentDragPosition.top + 'px' }"
  >
    <span @click="showControlPanel" class="icon-click-area" :title="`${serviceName}`">
      <service-icon-wrapper
        :id="`context-menu-${getService.id}`"
        :service="getService"
        class="svg-shadow"
      />
    </span>
    <div class="status d-flex justify-content-center">
      <statuses
        :service="getService"
      />
    </div>
    <font-awesome-layers
      v-if="spaceplan.isEditModeEnabled"
      @click="removeClicked"
      class="fa-lg delete-btn"
    >
      <font-awesome-icon icon="circle" />
      <font-awesome-icon icon="times" transform="shrink-6" :style="{ color: 'white' }" />
    </font-awesome-layers>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ControlPanel from '@/components/services/ControlPanel.vue';
import Statuses from '@/components/services/service/tile/Statuses.vue';
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import ServiceIconMixin from '@/mixins/ServiceIconMixin';
import ServiceIconWrapper from '@/components/services/service/ServiceIconWrapper';

export default {
  name: 'PlanItem',
  mixins: [
    ServiceIconMixin,
  ],
  props: {
    planItem: {
      type: Object,
      required: true,
    },
    editEnabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      initialPosition: undefined,
      initialPointerPos: undefined,
      startDragPosition: undefined,
      currentDragPosition: {
        top: this.planItem.top,
        left: this.planItem.left,
      },
      draggingEnabled: false,
      activeServicePopover: undefined,
    };
  },
  computed: {
    ...mapState('app', [
      'spaceplan',
    ]),
    ...mapGetters([
      'getServiceById',
      'getServiceName',
    ]),
    getService() {
      return this.getServiceById(this.planItem.service);
    },
    serviceName() {
      return this.getServiceName(this.getService.id);
    },
  },
  methods: {
    ...mapActions([
      'updateSpaceplanItem',
      'removeSpaceplanItem',
    ]),
    ...mapActions('app', [
      'setState',
    ]),
    boundingElement() {
      return this.$parent.$refs.boundingElement;
    },
    getBoundingElementPosition() {
      const clientRect = this.boundingElement().getBoundingClientRect();
      return {
        left: clientRect.left,
        top: clientRect.top,
      };
    },
    getRectPosition(el) {
      const clientRect = this.$el.getBoundingClientRect();
      return {
        left: clientRect.left
          - this.getBoundingElementPosition().left
          + this.boundingElement().scrollLeft,
        top: clientRect.top - this.getBoundingElementPosition().top,
      };
    },
    getRelativePointerPosition(e) {
      const pointerPosition = this.getPointerPosition(e);
      return {
        left: pointerPosition.left - this.getBoundingElementPosition().left,
        top: pointerPosition.top - this.getBoundingElementPosition().top,
      };
    },
    dragStart(e) {
      this.enableDragging();
      document.addEventListener('mousemove', this.move, { passive: false });
      document.addEventListener('touchmove', this.touchMove, { passive: false });
      document.addEventListener('mouseup', this.dragEnd);
      document.addEventListener('touchend', this.dragEnd);
    },
    dragEnd(e) {
      this.draggingEnabled = false;
      document.removeEventListener('mousemove', this.move);
      document.removeEventListener('touchmove', this.touchMove);
      document.removeEventListener('mouseup', this.dragEnd);
      document.removeEventListener('touchend', this.dragEnd);
      this.onPosChanged(this.currentDragPosition);
      this.initialPointerPos = undefined;
      this.startDragPosition = undefined;
    },
    initializeState(e) {
      const startingDragPosition = this.getRectPosition();
      this.startDragPosition = startingDragPosition;
      this.currentDragPosition = startingDragPosition;
      this.initialPointerPos = this.getRelativePointerPosition(e);
    },
    getPointerPosition(e) {
      let cursor = e;
      if (e.type === 'touchmove') {
        [cursor] = e.touches;
      }
      return {
        left: cursor.clientX,
        top: cursor.clientY,
      };
    },
    adjustPositionAgainstBoundaries({ left, top }) {
      const boundingRect = this.boundingElement().getBoundingClientRect();
      const adjustedPos = { left, top };
      const { height, width } = this.$el.getBoundingClientRect();

      const scrollLeftOffset = this.boundingElement().scrollLeft;

      const movingRect = {
        left,
        top,
        right: left + width,
        bottom: top + height,
      };
      const boundary = {
        top: 0,
        left: scrollLeftOffset,
        bottom: boundingRect.height,
        right: boundingRect.width + scrollLeftOffset,
      };
      if (movingRect.top < boundary.top) {
        adjustedPos.top = boundary.top;
      } else if (movingRect.bottom > boundary.bottom) {
        adjustedPos.top = boundary.bottom - height;
      }
      if (movingRect.left < boundary.left) {
        adjustedPos.left = boundary.left;
      } else if (movingRect.right > boundary.right) {
        adjustedPos.left = boundary.right - width;
      }
      return adjustedPos;
    },
    touchMove(e) {
      if (e.touches.length === 1) {
        this.move(e);
      }
    },
    move(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!this.draggingEnabled) {
        return;
      }
      if (!this.startDragPosition) {
        this.initializeState(e);
      }
      const pointerPosition = this.getRelativePointerPosition(e);
      const dx = pointerPosition.left - this.initialPointerPos.left;
      const dy = pointerPosition.top - this.initialPointerPos.top;
      this.currentDragPosition = this.adjustPositionAgainstBoundaries({
        left: this.startDragPosition.left + dx,
        top: this.startDragPosition.top + dy,
      });
    },
    onPosChanged(position) {
      this.updateSpaceplanItem({
        planItem: this.planItem,
        planItemData: {
          service: this.planItem.service,
          spaceplan: this.planItem.spaceplan,
          left: Math.round(position.left),
          top: Math.round(position.top),
        },
      });
    },
    showControlPanel() {
      if (!this.editEnabled) {
        this.$showPanel({
          component: ControlPanel,
          width: '500px',
          cssClass: 'panel-1-custom-class',
          keepAlive: false,
          props: {
            service: this.getService,
          },
        });
      }
    },
    enableDragging() {
      this.draggingEnabled = this.editEnabled;
    },
    onContextMenu() {
      const currentServiceCtxMenu = (
        this.spaceplan.currentServiceCtxMenu === this.getService.id
          ? undefined
          : this.getService.id
      );
      this.setState({ section: 'spaceplan', setting: { currentServiceCtxMenu } });
    },
    removeClicked() {
      this.$bvModal.msgBoxConfirm(
        `${this.$t('are-you-sure-you-want-remove')} ${this.getService.name} ?`,
      ).then((value) => {
        if (value) {
          this.removeSpaceplanItem(this.planItem);
        }
      }).catch((err) => {
        // An error occurred
      });
    },
  },
  components: {
    ServiceIconWrapper,
    Statuses,
    FontAwesomeLayers,
  },
};
</script>

<style scoped>

div.plan-item {
  position: absolute;
  padding: 10px;
  font-weight: bold;
  cursor: pointer;
  width: 50px;
  height: 50px;
}

.icon-click-area {
  z-index: 100;
  position: absolute;
  width: 30px;
  height: 30px;
}

.status {
  z-index: 5;
  position: absolute;
  top: 50px;
  width: 150px;
  transform: translateX(-40%);
}

.delete-btn {
  position: absolute;
  right: -5px;
  top: -5px;
}

</style>
